import React, { FC, useEffect, useMemo, useState } from 'react';
import { deliveryOptionsInterface, gunOptionsInterface, StepProps } from "../../types/types";
import {getBookingConfig, showDate} from "../../data/price";
import DatePicker from "react-datepicker";
import {
    getDay,
    nextFriday,
    nextThursday,
    subDays,
    isAfter,
    startOfToday,
    addDays,
    format,
    setHours,
    differenceInCalendarWeeks,
    getHours,
    isSameDay,
    parse, parseISO, isWithinInterval
} from "date-fns";
import { useTranslation } from "react-i18next";
import { HolidaysManager } from "../../data/HolidaysManager";
import { RentalPointDelivery } from "../../data/RentalPointDelivery";
import BookingService from "../../api/BookingService";
import LoadingIndicator from "../utils/LoadingIndicator";
import { useAppDispatch, useAppSelector } from '../../hooks';
import { setDeliveryDate, setReturnDate } from '../../stateReducers/deliverySlice';
import { RentalPointName } from '../../data/RentalPointName';

const RentalPointDatesSelector: FC<StepProps> = ({setStepValidity}) => {
    const {t} = useTranslation();
    const d = useAppDispatch();
    const formType = useAppSelector((state) => { return state.delivery.formType; });
    const deliveryOptions = useAppSelector((state) => { return state.delivery; });
    const gunOptions = useAppSelector((state) => { return state.gunOptions; });

    const [gunsAvailableForSelectedDates, setGunsAvailableForSelectedDates] = useState(true);

    const [checkingAvailability, setCheckingAvailability] = useState(false);
    const bookingConfig = getBookingConfig();
    const holidayManager = new HolidaysManager(bookingConfig, bookingConfig.rentalPointSchedule);
    const rentalPointsScheduler = new RentalPointDelivery(holidayManager);
    const isWorkingDate = (date: Date) => {
        if (!deliveryOptions.rentalPoint) {
            return true;
        }
        if (!bookingConfig.holidays.hasOwnProperty(deliveryOptions.rentalPoint)) {
            return true;
        }
        const dateString = format(date, "yyyy-MM-dd");
        if (bookingConfig.holidays[deliveryOptions.rentalPoint].includes(dateString)) {
            return false;
        }
        if (bookingConfig.globalHolidays.includes(dateString)) {
            return false;
        }
        let works = bookingConfig.rentalPointSchedule[deliveryOptions.rentalPoint].indexOf(getDay(date)) !== -1;
        // console.log('working date', format(date,"yyyy-MM-dd"),works);
        return works;
    };

    // Needs to be a separate function because the return date for a Beerse rental booking can't
    // be on a Friday (delivery date Friday is fine)
    const isWorkingReturnDate = (date: Date) => {
        if (isWorkingDate(date)) {
            if (deliveryOptions.rentalPoint === RentalPointName.BEERSE) {
                const friday = 5;
                if (date.getDay() === friday) {
                    return false;
                }
            }
        } else {
            return false;
        }
        return true;
    }

    const calculateMinReturnDate = () => {
        if (!deliveryOptions.playEnd || !deliveryOptions.rentalPoint) {
            return new Date();
        }
        const date = rentalPointsScheduler.findPointNextReturnDate(deliveryOptions.rentalPoint, deliveryOptions.playEnd);
        console.log("Min return:", date);
        return date;
    }

    const calculateMinPickupDate = () => {
        let checkedDate = deliveryOptions.playStart;
        let minPickupDate = deliveryOptions.playStart;
        if (!deliveryOptions.playStart || !deliveryOptions.rentalPoint) {
            minPickupDate = new Date();
        } else {
            if (formType === 'YouthPromo') {
                // For the YouthPromo, the pickup date should be as close
                // to the play date as possible.
                minPickupDate = maxPickupDate;
            } else {
                // Start on playStart, find previous working date of rental point until 2 weeks has been reached or until
                // it becomes sooner than 'tomorrow'
                while (differenceInCalendarWeeks(deliveryOptions.playStart, checkedDate!) < 2 && isAfter(checkedDate!, startOfToday())) {
                    checkedDate = rentalPointsScheduler.findPointPreviousWorkingDate(deliveryOptions.rentalPoint, checkedDate!);
                    if (differenceInCalendarWeeks(deliveryOptions.playStart, checkedDate) < 2 && isAfter(checkedDate, startOfToday())) {
                        minPickupDate = checkedDate;
                    }
                }
            }
        }
        
        console.log('min pickup', format(minPickupDate!, "yyyy-MM-dd"));
        return minPickupDate!;
    }
    const maxPickupDate: Date = useMemo(() => {
        if (!deliveryOptions.playStart || !deliveryOptions.rentalPoint) {
            return new Date();
        }

        return rentalPointsScheduler.findPointPreviousWorkingDate(deliveryOptions.rentalPoint, deliveryOptions.playStart);
    }, [deliveryOptions.playStart, deliveryOptions.startDate, deliveryOptions. rentalPoint]);

    const [minPickupDate] = useState<Date>(calculateMinPickupDate());
    const [maxReturnDate] = useState<Date>((function(){
        if (formType === 'YouthPromo') {
            // For the YouthPromo, it must be returned ASAP
            return calculateMinReturnDate();
        } else {
            if (deliveryOptions.rentalPoint === RentalPointName.BEERSE) {
                return nextThursday(deliveryOptions.playEnd ?? new Date());
            }
            const potentialMax = nextFriday(deliveryOptions.playEnd ?? new Date());
            const min = calculateMinReturnDate();
            if (min > potentialMax) {
                return min;
            } else {
                return potentialMax;
            }
        }
    })());

    const selectPickupDate = (date: Date) => {
        if (!date || !deliveryOptions.endDate) {
            return;
        }
        setCheckingAvailability(true);
        BookingService.checkGivenRentalPointAvailability(deliveryOptions.rentalPoint!,
            gunOptions.gunType!,
            gunOptions.quantity!,
            date,
            deliveryOptions.endDate,
            deliveryOptions.playStart!
        ).then((result) => {
            setGunsAvailableForSelectedDates(result.data[deliveryOptions.rentalPoint!]);
            setCheckingAvailability(false);
            d(setDeliveryDate(date));
        });
    }
    const selectReturnDate = (date: Date) => {
        if (!date || !deliveryOptions.startDate) {
            return;
        }
        setCheckingAvailability(true);
        BookingService.checkGivenRentalPointAvailability(deliveryOptions.rentalPoint!,
            gunOptions.gunType!,
            gunOptions.quantity!,
            deliveryOptions.startDate!,
            date,
            deliveryOptions.playStart!
        ).then((result) => {
            setCheckingAvailability(false);
            setGunsAvailableForSelectedDates(result.data[deliveryOptions.rentalPoint!]);

            d(setReturnDate(date));
        });

    }
    useEffect(() => {
        if (!deliveryOptions.returnDate) {
            d(setReturnDate(calculateMinReturnDate()));
        }
        if (!deliveryOptions.deliveryDate) {
            d(setDeliveryDate(maxPickupDate));
        }
        if (deliveryOptions.rentalPoint) {
            console.log('schedule', bookingConfig.rentalPointSchedule[deliveryOptions.rentalPoint]);
        }
        let isValid = !!deliveryOptions.returnDate && !!deliveryOptions.deliveryDate && gunsAvailableForSelectedDates;
        setStepValidity(isValid);
    });

    return (
        <div>
            <div className="form-group mb-6 flex flex-row">
                <div className="mb-3 xl:w-full">
                    <label htmlFor="pickupDate"
                           className="form-label inline-block mb-2 text-gray-700">{t('delivery.selectPickupDateLabel')}
                        <span
                            className="text-red-500 font-bold text-2xl">*</span> </label>
                    <DatePicker
                        className="form-control block w-full px-3 py-1.5 text-base font-normal text-gray-700 bg-white bg-clip-padding border border-solid border-gray-300 rounded transition ease-in-out m-0 focus:text-gray-700 focus:bg-white focus:border-orange-200 focus:shadow-none focus:outline-none focus:outline-none"
                        onChange={selectPickupDate}
                        selected={deliveryOptions.deliveryDate}
                        filterDate={isWorkingDate}
                        id="pickupDate"
                        placeholderText={t('delivery.selectDeliveryDatePlaceholder')}
                        minDate={minPickupDate}
                        maxDate={maxPickupDate}
                        calendarStartDay={1}
                        withPortal
                        portalId="modal-root"
                        dateFormat="dd-MM-yyyy"
                    />
                </div>
            </div>
            <div className="form-group mb-6 flex flex-row">
                <div className="mb-3 xl:w-full">
                    <label htmlFor="returnDate"
                           className="form-label inline-block mb-2 text-gray-700">{t('delivery.selectReturnDateLabel')}
                        <span
                            className="text-red-500 font-bold text-2xl">*</span> </label>
                    <DatePicker
                        className="form-control block w-full px-3 py-1.5 text-base font-normal text-gray-700 bg-white bg-clip-padding border border-solid border-gray-300 rounded transition ease-in-out m-0 focus:text-gray-700 focus:bg-white focus:border-orange-200 focus:shadow-none focus:outline-none focus:outline-none"
                        onChange={selectReturnDate}
                        selected={deliveryOptions.returnDate}
                        filterDate={isWorkingReturnDate}
                        id="returnDate"
                        placeholderText={t('delivery.selectReturnDatePlaceholder')}
                        minDate={calculateMinReturnDate()}
                        maxDate={maxReturnDate}
                        calendarStartDay={1}
                        withPortal
                        portalId="modal-root"
                        dateFormat="dd-MM-yyyy"
                    />
                </div>
            </div>
            {checkingAvailability &&
                <LoadingIndicator text={t("loadingRentalPointsLabel")}/>
            }
            {!checkingAvailability && !gunsAvailableForSelectedDates && <div className="form-group mb-6 flex flex-row text-orange-500 font-bold">
                {t("errors.noGunsForSelectedDates")}
            </div>}
        </div>
    );
};

export default RentalPointDatesSelector;
